import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useMessagesUnread } from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import usePushBus from '../hooks/usePushBus';
import { MessageSquare } from 'lucide-react';

/**
 * Round icon (matches `NotificationBell`) that opens the Messages inbox.
 * Shows a red badge with the total unread count across *accepted*
 * conversations. Pending requests are intentionally excluded from the
 * red badge — they get their own pill inside the Inbox tab.
 *
 * Reacts to FCM push notifications via `usePushBus` so the badge
 * refreshes in the same second a message arrives — no 30 s wait.
 */
export default function MessagesIcon() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useMessagesUnread({ enabled: !!user });
  const unread = data?.unread || 0;

  // On every incoming push (or window focus), refetch the badge
  // immediately. Filter to messaging pushes when we can identify them
  // — otherwise refetch unconditionally (cheap).
  const onPush = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
  }, [qc]);
  usePushBus(onPush);

  if (!user) return null;

  return (
    <button
      onClick={() => navigate('/app/messaggi')}
      data-testid="messages-icon"
      aria-label="Messages"
      className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
        isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
      }`}
    >
      <MessageSquare className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
      {unread > 0 && (
        <span
          data-testid="messages-unread-badge"
          className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 rounded-full bg-[#E8334A] text-white text-xs font-bold flex items-center justify-center"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}
